/**
 * Chat Settings Step
 * Configures chat details, type, and basic settings
 */

import type { CreateChatFormData } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiSettings, FiMessageCircle } from 'react-icons/fi';

import {
  Input as FormInput,
  TextArea,
} from '../../../../../../components/FormComponents';
import {
  FORM_LABELS,
  FORM_PLACEHOLDERS,
} from '../../../../constants/chatOptions';
import { ChatTypeSelect, VisibleChatPricesSection } from '../../../shared';

interface ChatSettingsStepProps {
  // No props needed - uses form context
}

export const ChatSettingsStep: React.FC<ChatSettingsStepProps> = () => {
  const { control } = useFormContext<CreateChatFormData>();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground">
          Configure Chat Settings
        </h3>
        <p className="text-sm text-foreground-600 mt-1">
          Set up the basic details and preferences for this chat
        </p>
      </div>

      <div className="space-y-6">
        {/* Basic Settings */}
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <FiMessageCircle className="text-primary" />
              <h4 className="font-medium">Basic Information</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="chatName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    id="chat-name"
                    name={field.name}
                    label={FORM_LABELS.chatName}
                    value={field.value || ''}
                    placeholder={FORM_PLACEHOLDERS.chatName}
                    required={false}
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="restaurantName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    id="restaurant-name"
                    name={field.name}
                    label={FORM_LABELS.restaurantName}
                    value={field.value || ''}
                    placeholder={FORM_PLACEHOLDERS.restaurantName}
                    required={true}
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <Controller
              name="deliveryAddress"
              control={control}
              render={({ field, fieldState }) => (
                <TextArea
                  id="delivery-address"
                  name={field.name}
                  label={FORM_LABELS.deliveryAddress}
                  value={field.value || ''}
                  placeholder={FORM_PLACEHOLDERS.deliveryAddress}
                  required={false}
                  isInvalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                  rows={2}
                  onChange={field.onChange}
                />
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="customerNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="space-y-1">
                    <FormInput
                      id="customer-number"
                      name={field.name}
                      label={FORM_LABELS.customerNumber}
                      value={field.value || ''}
                      placeholder={FORM_PLACEHOLDERS.customerNumber}
                      required={false}
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      onChange={field.onChange}
                    />
                    <p className="text-xs text-default-500">
                      May be required for some suppliers
                    </p>
                  </div>
                )}
              />

              <Controller
                name="additionalEmails"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="space-y-1">
                    <FormInput
                      id="additional-emails"
                      name={field.name}
                      label={FORM_LABELS.additionalEmails}
                      value={field.value || ''}
                      placeholder={FORM_PLACEHOLDERS.additionalEmails}
                      type="email"
                      required={false}
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      onChange={field.onChange}
                    />
                    <p className="text-xs text-default-500">
                      Comma-separated email addresses
                    </p>
                  </div>
                )}
              />
            </div>
          </CardBody>
        </Card>

        {/* Chat Type and Context */}
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <FiSettings className="text-secondary" />
              <h4 className="font-medium">Chat Configuration</h4>
            </div>

            <ChatTypeSelect required />
          </CardBody>
        </Card>

        {/* Price Configuration */}
        <VisibleChatPricesSection />
      </div>
    </div>
  );
};
